import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import Layout from '../components/Layout';

const Properties = () => {
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [propertyMetrics, setPropertyMetrics] = useState({});
  const [activeTab, setActiveTab] = useState('overview');
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [showEditPropertyModal, setShowEditPropertyModal] = useState(false);
  const [showEditUnitModal, setShowEditUnitModal] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [editProperty, setEditProperty] = useState({});
  const [editBuildings, setEditBuildings] = useState([]);
  const [showPropertyDetailsModal, setShowPropertyDetailsModal] = useState(false);
  const [viewingProperty, setViewingProperty] = useState(null);
  const [newProperty, setNewProperty] = useState({
    name: '',
    address: '',
    property_type: 'multifamily',
    units_count: 1,
    year_built: '',
    square_footage: '',
    buildings: [{ building_name: '', address: '', floors: 1, units_per_floor: 1 }]
  });
  const [newUnit, setNewUnit] = useState({
    unit_number: '',
    bedrooms: 1,
    bathrooms: 1,
    square_feet: '',
    rent_amount: '',
    deposit_amount: '',
    building_id: '',
    floor_number: '',
    unit_type: '1br'
  });
  const [financialData, setFinancialData] = useState({});
  const [maintenanceData, setMaintenanceData] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [showBuildingModal, setShowBuildingModal] = useState(false);
  const [newBuilding, setNewBuilding] = useState({
    building_name: '',
    building_number: '',
    address: '',
    floors: 1,
    units_per_floor: 1
  });

  const fetchProperties = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('properties')
        .select('*')
        .order('created_at', { ascending: false });
      setProperties(data || []);
      
      if (data && data.length > 0) {
        await calculatePropertyMetrics(data);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  }, []);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  const calculatePropertyMetrics = async (properties) => {
    const metrics = {};
    for (const property of properties) {
      const { data: units } = await supabase
        .from('units')
        .select('*, leases(rent_amount, status)')
        .eq('property_id', property.id);
      
      const occupiedUnits = units?.filter(u => u.status === 'occupied').length || 0;
      const totalUnits = units?.length || 0;
      const occupancyRate = totalUnits > 0 ? (occupiedUnits / totalUnits) * 100 : 0;
      const monthlyRent = units?.reduce((sum, u) => sum + (parseFloat(u.rent_amount) || 0), 0) || 0;
      
      metrics[property.id] = {
        occupancyRate,
        monthlyRent,
        occupiedUnits,
        totalUnits
      };
    }
    setPropertyMetrics(metrics);
  };

  const fetchUnits = async (propertyId) => {
    try {
      // Get all units for the property
      const { data: allUnits } = await supabase
        .from('units')
        .select('*')
        .eq('property_id', propertyId)
        .order('unit_number');
      
      // Get units with active leases and resident info
      const { data: unitsWithLeases } = await supabase
        .from('units')
        .select(`
          id,
          leases!inner(
            status,
            residents(
              first_name,
              last_name
            )
          )
        `)
        .eq('property_id', propertyId)
        .eq('leases.status', 'active');
      
      // Merge the data
      const unitsWithResidents = allUnits?.map(unit => {
        const leaseInfo = unitsWithLeases?.find(u => u.id === unit.id);
        return {
          ...unit,
          leases: leaseInfo?.leases || null
        };
      });
      
      setUnits(unitsWithResidents || []);
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  };

  const selectProperty = async (property) => {
    setSelectedProperty(property);
    await fetchUnits(property.id);
    await fetchFinancialData(property.id);
    await fetchMaintenanceData(property.id);
    if (property.property_type === 'apartment_complex') {
      await fetchBuildings(property.id);
    }
    // Recalculate metrics for this property to ensure accurate counts
    await calculatePropertyMetrics([property]);
  };

  const fetchBuildings = async (propertyId) => {
    try {
      const { data } = await supabase
        .from('buildings')
        .select('*')
        .eq('property_id', propertyId)
        .order('building_name');
      setBuildings(data || []);
    } catch (error) {
      console.error('Error fetching buildings:', error);
    }
  };

  const handleAddBuilding = async (e) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from('buildings')
        .insert([{
          ...newBuilding,
          property_id: selectedProperty.id
        }]);
      
      if (error) throw error;
      
      setShowBuildingModal(false);
      setNewBuilding({
        building_name: '',
        building_number: '',
        address: '',
        floors: 1,
        units_per_floor: 1
      });
      await fetchBuildings(selectedProperty.id);
    } catch (error) {
      console.error('Error adding building:', error);
    }
  };

  const fetchFinancialData = async (propertyId) => {
    try {
      const currentMonth = new Date();
      const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
      
      // Get monthly income from rent payments
      const { data: rentPayments } = await supabase
        .from('rent_payments')
        .select('amount, leases(unit_id)')
        .eq('status', 'paid')
        .gte('paid_date', startOfMonth.toISOString().split('T')[0])
        .lte('paid_date', endOfMonth.toISOString().split('T')[0]);
      
      // Filter for this property's units
      const { data: propertyUnits } = await supabase
        .from('units')
        .select('id')
        .eq('property_id', propertyId);
      
      const unitIds = propertyUnits?.map(u => u.id) || [];
      const monthlyIncome = rentPayments?.filter(p => 
        unitIds.includes(p.leases?.unit_id)
      ).reduce((sum, p) => sum + parseFloat(p.amount), 0) || 0;
      
      // Get monthly expenses from maintenance
      const { data: maintenanceExpenses } = await supabase
        .from('maintenance_requests')
        .select('actual_cost, unit_id')
        .in('unit_id', unitIds)
        .not('actual_cost', 'is', null)
        .gte('completed_date', startOfMonth.toISOString().split('T')[0])
        .lte('completed_date', endOfMonth.toISOString().split('T')[0]);
      
      const monthlyExpenses = maintenanceExpenses?.reduce((sum, m) => sum + parseFloat(m.actual_cost || 0), 0) || 0;
      const netIncome = monthlyIncome - monthlyExpenses;
      
      setFinancialData({
        monthlyIncome,
        monthlyExpenses,
        netIncome,
        capRate: 7.4 // Placeholder calculation
      });
    } catch (error) {
      console.error('Error fetching financial data:', error);
    }
  };

  const fetchMaintenanceData = async (propertyId) => {
    try {
      const { data: propertyUnits } = await supabase
        .from('units')
        .select('id')
        .eq('property_id', propertyId);
      
      const unitIds = propertyUnits?.map(u => u.id) || [];
      
      const { data: maintenance } = await supabase
        .from('maintenance_requests')
        .select('*')
        .in('unit_id', unitIds)
        .order('created_at', { ascending: false })
        .limit(5);
      
      setMaintenanceData(maintenance || []);
    } catch (error) {
      console.error('Error fetching maintenance data:', error);
    }
  };

  const handleAddProperty = async (e) => {
    e.preventDefault();
    try {
      const propertyData = {
        name: newProperty.name,
        address: newProperty.address,
        property_type: newProperty.property_type,
        units_count: newProperty.units_count,
        year_built: newProperty.year_built,
        square_footage: newProperty.square_footage
      };
      
      const { data: property, error } = await supabase
        .from('properties')
        .insert([propertyData])
        .select()
        .single();
      
      if (error) throw error;
      
      // If apartment complex, create buildings
      if (newProperty.property_type === 'apartment_complex' && newProperty.buildings.length > 0) {
        const buildingsData = newProperty.buildings.map(building => ({
          ...building,
          property_id: property.id
        }));
        
        const { error: buildingsError } = await supabase
          .from('buildings')
          .insert(buildingsData);
        
        if (buildingsError) throw buildingsError;
      }
      
      setShowPropertyModal(false);
      setNewProperty({
        name: '',
        address: '',
        property_type: 'multifamily',
        units_count: 1,
        year_built: '',
        square_footage: '',
        buildings: [{ building_name: '', address: '', floors: 1, units_per_floor: 1 }]
      });
      await fetchProperties();
    } catch (error) {
      console.error('Error adding property:', error);
    }
  };

  const addBuildingToProperty = () => {
    setNewProperty({
      ...newProperty,
      buildings: [...newProperty.buildings, { building_name: '', address: '', floors: 1, units_per_floor: 1 }]
    });
  };

  const removeBuildingFromProperty = (index) => {
    const updatedBuildings = newProperty.buildings.filter((_, i) => i !== index);
    setNewProperty({ ...newProperty, buildings: updatedBuildings });
  };

  const updateBuildingInProperty = (index, field, value) => {
    const updatedBuildings = newProperty.buildings.map((building, i) => 
      i === index ? { ...building, [field]: value } : building
    );
    setNewProperty({ ...newProperty, buildings: updatedBuildings });
  };

  const handleAddUnit = async (e) => {
    e.preventDefault();
    try {
      const unitData = {
        ...newUnit,
        property_id: selectedProperty.id
      };
      
      // Remove building_id if it's empty to avoid UUID error
      if (!unitData.building_id) {
        delete unitData.building_id;
      }
      
      const { error } = await supabase
        .from('units')
        .insert([unitData]);
      
      if (error) throw error;
      
      setShowUnitModal(false);
      setNewUnit({
        unit_number: '',
        bedrooms: 1,
        bathrooms: 1,
        square_feet: '',
        rent_amount: '',
        deposit_amount: ''
      });
      await fetchUnits(selectedProperty.id);
      await fetchProperties();
    } catch (error) {
      console.error('Error adding unit:', error);
    }
  };

  const updateUnitStatus = async (unitId, newStatus) => {
    try {
      const { error } = await supabase
        .from('units')
        .update({ status: newStatus })
        .eq('id', unitId);
      
      if (error) throw error;
      
      await fetchUnits(selectedProperty.id);
      await fetchProperties();
    } catch (error) {
      console.error('Error updating unit status:', error);
    }
  };

  const moveUnitToBuilding = async (unitId, buildingId) => {
    try {
      const { error } = await supabase
        .from('units')
        .update({ building_id: buildingId || null })
        .eq('id', unitId);
      
      if (error) throw error;
      
      await fetchUnits(selectedProperty.id);
    } catch (error) {
      console.error('Error moving unit to building:', error);
    }
  };

  const generateUnitsForBuilding = async (building) => {
    if (!window.confirm(`Generate ${building.floors * building.units_per_floor} units for ${building.building_name}?`)) {
      return;
    }
    
    try {
      const unitsToCreate = [];
      
      for (let floor = 1; floor <= building.floors; floor++) {
        for (let unitOnFloor = 1; unitOnFloor <= building.units_per_floor; unitOnFloor++) {
          const unitNumber = `${floor}${unitOnFloor.toString().padStart(2, '0')}`;
          
          unitsToCreate.push({
            property_id: selectedProperty.id,
            building_id: building.id,
            unit_number: unitNumber,
            floor_number: floor,
            bedrooms: 1,
            bathrooms: 1,
            rent_amount: 1000,
            deposit_amount: 1000,
            unit_type: '1br',
            status: 'vacant'
          });
        }
      }
      
      const { error } = await supabase
        .from('units')
        .insert(unitsToCreate);
      
      if (error) throw error;
      
      await fetchUnits(selectedProperty.id);
      await fetchProperties();
      alert(`Generated ${unitsToCreate.length} units for ${building.building_name}`);
    } catch (error) {
      console.error('Error generating units:', error);
      alert('Error generating units. Please try again.');
    }
  };

  const handleEditProperty = async (e) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from('properties')
        .update(editProperty)
        .eq('id', selectedProperty.id);
      
      if (error) throw error;
      
      // Update buildings if apartment complex
      if (editProperty.property_type === 'apartment_complex') {
        // Delete existing buildings and recreate (simple approach)
        await supabase.from('buildings').delete().eq('property_id', selectedProperty.id);
        
        if (editBuildings.length > 0) {
          const buildingsData = editBuildings.map(building => ({
            building_name: building.building_name,
            building_number: building.building_number,
            address: building.address,
            floors: building.floors,
            units_per_floor: building.units_per_floor,
            property_id: selectedProperty.id
          }));
          
          await supabase.from('buildings').insert(buildingsData);
        }
      }
      
      setShowEditPropertyModal(false);
      setSelectedProperty(editProperty);
      await fetchProperties();
      if (editProperty.property_type === 'apartment_complex') {
        await fetchBuildings(selectedProperty.id);
      }
    } catch (error) {
      console.error('Error updating property:', error);
    }
  };

  const addBuildingToEdit = () => {
    setEditBuildings([...editBuildings, { building_name: '', address: '', floors: 1, units_per_floor: 1 }]);
  };

  const removeBuildingFromEdit = (index) => {
    setEditBuildings(editBuildings.filter((_, i) => i !== index));
  };

  const updateBuildingInEdit = (index, field, value) => {
    const updatedBuildings = editBuildings.map((building, i) => 
      i === index ? { ...building, [field]: value } : building
    );
    setEditBuildings(updatedBuildings);
  };

  const handleEditUnit = async (e) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from('units')
        .update(newUnit)
        .eq('id', editingUnit.id);
      
      if (error) throw error;
      
      setShowEditUnitModal(false);
      setEditingUnit(null);
      setNewUnit({
        unit_number: '',
        bedrooms: 1,
        bathrooms: 1,
        square_feet: '',
        rent_amount: '',
        deposit_amount: ''
      });
      await fetchUnits(selectedProperty.id);
      await fetchProperties();
    } catch (error) {
      console.error('Error updating unit:', error);
    }
  };

  const openPropertyDetails = (property) => {
    setViewingProperty(property);
    setShowPropertyDetailsModal(true);
  };

  return (
    <Layout>
      <div style={{ padding: '24px', background: '#f8fafc', minHeight: '100vh' }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '32px',
          background: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '700', color: '#1e293b' }}>Properties</h1>
            <p style={{ margin: 0, color: '#64748b', fontSize: '16px' }}>Manage your real estate portfolio</p>
          </div>
          <button 
            onClick={() => setShowPropertyModal(true)}
            style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
              transition: 'all 0.2s'
            }}
          >
            + Add Property
          </button>
        </div>

        {/* Metrics Cards */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', 
          gap: '20px', 
          marginBottom: '32px' 
        }}>
          <div style={{ 
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', 
            color: 'white',
            borderRadius: '12px', 
            padding: '24px',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: 0.9 }}>Total Properties</p>
                <p style={{ margin: 0, fontSize: '32px', fontWeight: '700' }}>{properties.length}</p>
              </div>
              <div style={{ fontSize: '24px', opacity: 0.8 }}>🏢</div>
            </div>
          </div>
          <div style={{ 
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
            color: 'white',
            borderRadius: '12px', 
            padding: '24px',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: 0.9 }}>Total Units</p>
                <p style={{ margin: 0, fontSize: '32px', fontWeight: '700' }}>
                  {Object.values(propertyMetrics).reduce((sum, m) => sum + m.totalUnits, 0)}
                </p>
              </div>
              <div style={{ fontSize: '24px', opacity: 0.8 }}>🏠</div>
            </div>
          </div>
          <div style={{ 
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', 
            color: 'white',
            borderRadius: '12px', 
            padding: '24px',
            boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: 0.9 }}>Avg Occupancy</p>
                <p style={{ margin: 0, fontSize: '32px', fontWeight: '700' }}>
                  {Object.values(propertyMetrics).length > 0 ? 
                    Math.round(Object.values(propertyMetrics).reduce((sum, m) => sum + m.occupancyRate, 0) / Object.values(propertyMetrics).length) : 0}%
                </p>
              </div>
              <div style={{ fontSize: '24px', opacity: 0.8 }}>📊</div>
            </div>
          </div>
          <div style={{ 
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', 
            color: 'white',
            borderRadius: '12px', 
            padding: '24px',
            boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: 0.9 }}>Monthly Revenue</p>
                <p style={{ margin: 0, fontSize: '32px', fontWeight: '700' }}>
                  ${Object.values(propertyMetrics).reduce((sum, m) => sum + m.monthlyRent, 0).toLocaleString()}
                </p>
              </div>
              <div style={{ fontSize: '24px', opacity: 0.8 }}>💰</div>
            </div>
          </div>
        </div>

        {/* Properties Grid */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', 
          gap: '24px' 
        }}>
          {properties.map(property => {
            const metrics = propertyMetrics[property.id];
            const occupancyColor = metrics ? 
              (metrics.occupancyRate >= 90 ? '#10b981' : 
               metrics.occupancyRate >= 70 ? '#f59e0b' : '#ef4444') : '#6b7280';
            
            return (
              <div 
                key={property.id}
                onClick={() => selectProperty(property)}
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: '24px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  border: '1px solid #e2e8f0',
                  transition: 'all 0.2s',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
                }}
              >
                {/* Property Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ 
                      margin: '0 0 8px 0', 
                      fontSize: '20px', 
                      fontWeight: '700', 
                      color: '#1e293b',
                      lineHeight: '1.2'
                    }}>
                      {property.name}
                    </h3>
                    <p style={{ 
                      margin: '0 0 12px 0', 
                      color: '#64748b', 
                      fontSize: '14px',
                      lineHeight: '1.4'
                    }}>
                      📍 {property.address}
                    </p>
                    <div style={{
                      display: 'inline-block',
                      background: property.property_type === 'apartment_complex' ? '#dbeafe' : 
                                 property.property_type === 'single_family' ? '#dcfce7' : '#fef3c7',
                      color: property.property_type === 'apartment_complex' ? '#1e40af' : 
                             property.property_type === 'single_family' ? '#166534' : '#92400e',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '600',
                      textTransform: 'capitalize'
                    }}>
                      {property.property_type.replace('_', ' ')}
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openPropertyDetails(property);
                    }}
                    style={{
                      background: 'none',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '8px',
                      cursor: 'pointer',
                      color: '#64748b',
                      fontSize: '16px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.color = '#3b82f6';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.color = '#64748b';
                    }}
                  >
                    👁️
                  </button>
                </div>

                {/* Metrics */}
                {metrics && (
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(3, 1fr)', 
                    gap: '16px',
                    marginBottom: '16px'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ margin: '0 0 4px 0', fontSize: '24px', fontWeight: '700', color: '#1e293b' }}>
                        {metrics.totalUnits}
                      </p>
                      <p style={{ margin: 0, fontSize: '12px', color: '#64748b', fontWeight: '500' }}>Units</p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ 
                        margin: '0 0 4px 0', 
                        fontSize: '24px', 
                        fontWeight: '700', 
                        color: occupancyColor
                      }}>
                        {Math.round(metrics.occupancyRate)}%
                      </p>
                      <p style={{ margin: 0, fontSize: '12px', color: '#64748b', fontWeight: '500' }}>Occupied</p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: '700', color: '#1e293b' }}>
                        ${metrics.monthlyRent.toLocaleString()}
                      </p>
                      <p style={{ margin: 0, fontSize: '12px', color: '#64748b', fontWeight: '500' }}>Monthly</p>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openPropertyDetails(property);
                    }}
                    style={{
                      flex: 1,
                      background: '#f1f5f9',
                      color: '#475569',
                      border: 'none',
                      padding: '10px 16px',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#e2e8f0';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#f1f5f9';
                    }}
                  >
                    👁️ Details
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setNewUnit({ ...newUnit, building_id: '' });
                      setSelectedProperty(property);
                      setShowUnitModal(true);
                    }}
                    style={{
                      flex: 1,
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '10px 16px',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    + Add Unit
                  </button>
                </div>
              </div>
            );
          })}

        </div>

        {/* Property Details Modal */}
        {showPropertyDetailsModal && viewingProperty && (
          <div style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            background: 'rgba(0,0,0,0.6)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            zIndex: 1000,
            backdropFilter: 'blur(4px)'
          }}>
            <div style={{ 
              background: 'white', 
              borderRadius: '16px', 
              width: '90%', 
              maxWidth: '800px',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}>
              {/* Modal Header */}
              <div style={{ 
                padding: '24px 32px', 
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
              }}>
                <div>
                  <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '700', color: '#1e293b' }}>
                    {viewingProperty.name}
                  </h2>
                  <p style={{ margin: 0, color: '#64748b', fontSize: '16px' }}>
                    📍 {viewingProperty.address}
                  </p>
                </div>
                <button 
                  onClick={() => setShowPropertyDetailsModal(false)}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    fontSize: '24px', 
                    cursor: 'pointer', 
                    color: '#64748b',
                    padding: '8px',
                    borderRadius: '8px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#f1f5f9';
                    e.target.style.color = '#1e293b';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'none';
                    e.target.style.color = '#64748b';
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Modal Content */}
              <div style={{ padding: '32px' }}>
                {/* Property Overview */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                  gap: '20px',
                  marginBottom: '32px'
                }}>
                  <div style={{ 
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    color: 'white',
                    padding: '20px',
                    borderRadius: '12px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>
                      {propertyMetrics[viewingProperty.id]?.totalUnits || 0}
                    </div>
                    <div style={{ fontSize: '14px', opacity: 0.9 }}>Total Units</div>
                  </div>
                  <div style={{ 
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    padding: '20px',
                    borderRadius: '12px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>
                      {propertyMetrics[viewingProperty.id] ? Math.round(propertyMetrics[viewingProperty.id].occupancyRate) : 0}%
                    </div>
                    <div style={{ fontSize: '14px', opacity: 0.9 }}>Occupancy Rate</div>
                  </div>
                  <div style={{ 
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: 'white',
                    padding: '20px',
                    borderRadius: '12px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>
                      ${propertyMetrics[viewingProperty.id]?.monthlyRent.toLocaleString() || '0'}
                    </div>
                    <div style={{ fontSize: '14px', opacity: 0.9 }}>Monthly Revenue</div>
                  </div>
                </div>

                {/* Property Details */}
                <div style={{ 
                  background: '#f8fafc',
                  padding: '24px',
                  borderRadius: '12px',
                  marginBottom: '24px'
                }}>
                  <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                    Property Information
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                    <div>
                      <p style={{ margin: '0 0 4px 0', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Type</p>
                      <p style={{ margin: 0, fontSize: '16px', fontWeight: '500', color: '#1e293b', textTransform: 'capitalize' }}>
                        {viewingProperty.property_type.replace('_', ' ')}
                      </p>
                    </div>
                    {viewingProperty.year_built && (
                      <div>
                        <p style={{ margin: '0 0 4px 0', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Year Built</p>
                        <p style={{ margin: 0, fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                          {viewingProperty.year_built}
                        </p>
                      </div>
                    )}
                    {viewingProperty.square_footage && (
                      <div>
                        <p style={{ margin: '0 0 4px 0', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Square Footage</p>
                        <p style={{ margin: 0, fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                          {viewingProperty.square_footage.toLocaleString()} sq ft
                        </p>
                      </div>
                    )}
                    {propertyMetrics[viewingProperty.id] && (
                      <div>
                        <p style={{ margin: '0 0 4px 0', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Occupied Units</p>
                        <p style={{ margin: 0, fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                          {propertyMetrics[viewingProperty.id].occupiedUnits}/{propertyMetrics[viewingProperty.id].totalUnits}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      setShowPropertyDetailsModal(false);
                      selectProperty(viewingProperty);
                    }}
                    style={{
                      background: '#f1f5f9',
                      color: '#475569',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    📊 Manage Property
                  </button>
                  <button
                    onClick={() => {
                      setShowPropertyDetailsModal(false);
                      setSelectedProperty(viewingProperty);
                      setNewUnit({ ...newUnit, building_id: '' });
                      setShowUnitModal(true);
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    + Add Unit
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Management Panel */}
        {selectedProperty && (
          <>
            {/* Overlay */}
            <div 
              onClick={() => setSelectedProperty(null)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.3)',
                zIndex: 998,
                backdropFilter: 'blur(2px)'
              }}
            />
            {/* Slide-out Panel */}
            <div style={{
              position: 'fixed',
              top: 0,
              right: '0',
              width: '600px',
              height: '100vh',
              background: 'white',
              boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
              zIndex: 999,
              transition: 'right 0.3s ease',
              overflow: 'auto'
            }}>
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#1e293b' }}>
                  {selectedProperty.name}
                </h3>
                <button 
                  onClick={() => setSelectedProperty(null)}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    fontSize: '20px', 
                    cursor: 'pointer', 
                    color: '#64748b',
                    padding: '8px',
                    borderRadius: '6px'
                  }}
                >
                  ✕
                </button>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3>{selectedProperty.name}</h3>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button 
                      className="btn btn-secondary"
                      onClick={async () => {
                        setEditProperty(selectedProperty);
                        if (selectedProperty.property_type === 'apartment_complex') {
                          await fetchBuildings(selectedProperty.id);
                          setEditBuildings([...buildings]);
                        }
                        setShowEditPropertyModal(true);
                      }}
                    >
                      Edit Property
                    </button>
                    <button 
                      className="btn btn-primary"
                      onClick={() => setShowUnitModal(true)}
                    >
                      Add Unit
                    </button>
                  </div>
                </div>

                {/* Property Tabs */}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                  <button 
                    onClick={() => setActiveTab('overview')}
                    className={`btn ${activeTab === 'overview' ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ fontSize: '14px' }}
                  >
                    Overview
                  </button>
                  <button 
                    onClick={() => setActiveTab('units')}
                    className={`btn ${activeTab === 'units' ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ fontSize: '14px' }}
                  >
                    Units ({units.length})
                  </button>
                  <button 
                    onClick={() => setActiveTab('financials')}
                    className={`btn ${activeTab === 'financials' ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ fontSize: '14px' }}
                  >
                    Financials
                  </button>
                  <button 
                    onClick={() => setActiveTab('maintenance')}
                    className={`btn ${activeTab === 'maintenance' ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ fontSize: '14px' }}
                  >
                    Maintenance
                  </button>
                </div>

                {activeTab === 'overview' && (
                  <div>
                    <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <div>
                          <p><strong>Address:</strong> {selectedProperty.address}</p>
                          <p><strong>Type:</strong> {selectedProperty.property_type}</p>
                          <p><strong>Units:</strong> {propertyMetrics[selectedProperty.id]?.totalUnits || 0} 
                            {buildings.length > 0 && (
                              <span style={{ color: '#666', fontSize: '12px' }}>
                                (Capacity: {buildings.reduce((sum, b) => sum + (b.floors * b.units_per_floor), 0)})
                              </span>
                            )}
                          </p>
                          {selectedProperty.year_built && <p><strong>Year Built:</strong> {selectedProperty.year_built}</p>}
                          {selectedProperty.square_footage && <p><strong>Square Footage:</strong> {selectedProperty.square_footage.toLocaleString()} sq ft</p>}
                        </div>
                        <div>
                          {propertyMetrics[selectedProperty.id] && (
                            <div>
                              <p><strong>Occupancy Rate:</strong> {Math.round(propertyMetrics[selectedProperty.id].occupancyRate)}%</p>
                              <p><strong>Monthly Revenue:</strong> ${propertyMetrics[selectedProperty.id].monthlyRent.toLocaleString()}</p>
                              <p><strong>Occupied Units:</strong> {propertyMetrics[selectedProperty.id].occupiedUnits}/{propertyMetrics[selectedProperty.id].totalUnits}</p>
                            </div>
                          )}
                          {selectedProperty.purchase_price && <p><strong>Purchase Price:</strong> ${selectedProperty.purchase_price.toLocaleString()}</p>}
                          {selectedProperty.market_value && <p><strong>Market Value:</strong> ${selectedProperty.market_value.toLocaleString()}</p>}
                        </div>
                      </div>
                    </div>
                    
                    {selectedProperty.property_type === 'apartment_complex' && buildings.length > 0 && (
                      <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                          <h4>Buildings</h4>
                          <button 
                            className="btn btn-primary"
                            onClick={() => setShowBuildingModal(true)}
                            style={{ fontSize: '12px', padding: '6px 12px' }}
                          >
                            Add Building
                          </button>
                        </div>
                        <div style={{ display: 'grid', gap: '10px' }}>
                          {buildings.map(building => (
                            <div key={building.id} style={{ 
                              padding: '15px', 
                              background: '#f8f9fa', 
                              borderRadius: '6px',
                              border: '1px solid #e9ecef'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                <div>
                                  <h5 style={{ margin: '0 0 5px 0' }}>{building.building_name}</h5>
                                  <p style={{ margin: '0 0 5px 0', color: '#666', fontSize: '14px' }}>{building.address}</p>
                                  <div style={{ display: 'flex', gap: '15px', fontSize: '12px', color: '#666' }}>
                                    <span>Floors: {building.floors}</span>
                                    <span>Units per Floor: {building.units_per_floor}</span>
                                    <span>Total Units: {building.floors * building.units_per_floor}</span>
                                  </div>
                                </div>
                                <button 
                                  className="btn btn-secondary"
                                  style={{ fontSize: '11px', padding: '4px 8px' }}
                                  onClick={() => alert(`Building: ${building.building_name}\nAddress: ${building.address}\nFloors: ${building.floors}\nUnits per Floor: ${building.units_per_floor}`)}
                                >
                                  View Details
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'units' && (
                  <div>
                    {selectedProperty.property_type === 'apartment_complex' && buildings.length > 0 ? (
                      <div>
                        {buildings.map(building => {
                          const buildingUnits = units.filter(unit => unit.building_id === building.id);
                          const unassignedUnits = units.filter(unit => !unit.building_id);
                          
                          return (
                            <div key={building.id} style={{ marginBottom: '30px' }}>
                              <div style={{ 
                                background: '#f8f9fa', 
                                padding: '15px', 
                                borderRadius: '8px', 
                                marginBottom: '15px',
                                border: '2px solid #e9ecef'
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <div>
                                    <h4 style={{ margin: '0 0 5px 0' }}>{building.building_name}</h4>
                                    <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                                      {building.address} • {buildingUnits.length}/{building.floors * building.units_per_floor} units
                                      <span style={{ 
                                        marginLeft: '10px',
                                        color: buildingUnits.length === building.floors * building.units_per_floor ? '#28a745' : '#dc3545',
                                        fontWeight: 'bold',
                                        fontSize: '12px'
                                      }}>
                                        {buildingUnits.length === building.floors * building.units_per_floor ? '(Full)' : `(${building.floors * building.units_per_floor - buildingUnits.length} missing)`}
                                      </span>
                                    </p>
                                  </div>
                                  <div style={{ display: 'flex', gap: '8px' }}>
                                    <button 
                                      className="btn btn-secondary"
                                      onClick={() => generateUnitsForBuilding(building)}
                                      style={{ fontSize: '11px', padding: '4px 8px' }}
                                    >
                                      Generate Units
                                    </button>
                                    <button 
                                      className="btn btn-primary"
                                      onClick={() => {
                                        setNewUnit({ ...newUnit, building_id: building.id });
                                        setShowUnitModal(true);
                                      }}
                                      style={{ fontSize: '11px', padding: '4px 8px' }}
                                    >
                                      Add Unit
                                    </button>
                                  </div>
                                </div>
                              </div>
                              
                              <div style={{ display: 'grid', gap: '10px', marginLeft: '20px' }}>
                                {buildingUnits.map(unit => (
                                  <div key={unit.id} style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '15px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <div>
                                        <strong>Unit {unit.unit_number}</strong>
                                        <p style={{ margin: '5px 0', color: '#666' }}>
                                          {unit.unit_type || `${unit.bedrooms}BR/${unit.bathrooms}BA`} - {unit.square_feet} sq ft
                                          {unit.floor_number && ` - Floor ${unit.floor_number}`}
                                        </p>
                                        <p style={{ margin: '5px 0', fontWeight: 'bold' }}>
                                          ${unit.rent_amount}/month
                                        </p>
                                        {unit.leases && unit.leases[0]?.residents && (
                                          <p style={{ margin: '5px 0', color: '#10b981', fontSize: '12px', fontWeight: '500' }}>
                                            👤 {unit.leases[0].residents.first_name} {unit.leases[0].residents.last_name}
                                          </p>
                                        )}
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <select
                                          value={unit.building_id || ''}
                                          onChange={(e) => moveUnitToBuilding(unit.id, e.target.value)}
                                          style={{
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            fontSize: '11px',
                                            border: '1px solid #ddd'
                                          }}
                                        >
                                          <option value="">No Building</option>
                                          {buildings.map(b => (
                                            <option key={b.id} value={b.id}>{b.building_name}</option>
                                          ))}
                                        </select>
                                        <select
                                          value={unit.status}
                                          onChange={(e) => updateUnitStatus(unit.id, e.target.value)}
                                          style={{
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            border: '1px solid #ddd'
                                          }}
                                        >
                                          <option value="vacant">Vacant</option>
                                          <option value="occupied">Occupied</option>
                                          <option value="maintenance">Maintenance</option>
                                        </select>
                                        <button 
                                          className="btn btn-secondary" 
                                          style={{ fontSize: '12px', padding: '4px 8px' }}
                                          onClick={() => {
                                            setEditingUnit(unit);
                                            setNewUnit({
                                              unit_number: unit.unit_number,
                                              bedrooms: unit.bedrooms,
                                              bathrooms: unit.bathrooms,
                                              square_feet: unit.square_feet,
                                              rent_amount: unit.rent_amount,
                                              deposit_amount: unit.deposit_amount,
                                              building_id: unit.building_id,
                                              floor_number: unit.floor_number,
                                              unit_type: unit.unit_type
                                            });
                                            setShowEditUnitModal(true);
                                          }}
                                        >
                                          Edit
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                                {buildingUnits.length === 0 && (
                                  <p style={{ color: '#999', fontStyle: 'italic', marginLeft: '15px' }}>No units assigned to this building</p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        
                        {units.filter(unit => !unit.building_id).length > 0 && (
                          <div>
                            <div style={{ 
                              background: '#fff3cd', 
                              padding: '15px', 
                              borderRadius: '8px', 
                              marginBottom: '15px',
                              border: '2px solid #ffeaa7'
                            }}>
                              <h4 style={{ margin: '0 0 5px 0' }}>Unassigned Units</h4>
                              <p style={{ margin: 0, color: '#856404', fontSize: '14px' }}>
                                {units.filter(unit => !unit.building_id).length} units not assigned to any building
                              </p>
                            </div>
                            
                            <div style={{ display: 'grid', gap: '10px', marginLeft: '20px' }}>
                              {units.filter(unit => !unit.building_id).map(unit => (
                                <div key={unit.id} style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '15px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                      <strong>Unit {unit.unit_number}</strong>
                                      <p style={{ margin: '5px 0', color: '#666' }}>
                                        {unit.unit_type || `${unit.bedrooms}BR/${unit.bathrooms}BA`} - {unit.square_feet} sq ft
                                        {unit.floor_number && ` - Floor ${unit.floor_number}`}
                                      </p>
                                      <p style={{ margin: '5px 0', fontWeight: 'bold' }}>
                                        ${unit.rent_amount}/month
                                      </p>
                                      {unit.leases && unit.leases[0]?.residents && (
                                        <p style={{ margin: '5px 0', color: '#10b981', fontSize: '12px', fontWeight: '500' }}>
                                          👤 {unit.leases[0].residents.first_name} {unit.leases[0].residents.last_name}
                                        </p>
                                      )}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                      <select
                                        value={unit.building_id || ''}
                                        onChange={(e) => moveUnitToBuilding(unit.id, e.target.value)}
                                        style={{
                                          padding: '4px 8px',
                                          borderRadius: '4px',
                                          fontSize: '11px',
                                          border: '1px solid #ddd'
                                        }}
                                      >
                                        <option value="">Assign to Building</option>
                                        {buildings.map(b => (
                                          <option key={b.id} value={b.id}>{b.building_name}</option>
                                        ))}
                                      </select>
                                      <select
                                        value={unit.status}
                                        onChange={(e) => updateUnitStatus(unit.id, e.target.value)}
                                        style={{
                                          padding: '4px 8px',
                                          borderRadius: '4px',
                                          fontSize: '12px',
                                          border: '1px solid #ddd'
                                        }}
                                      >
                                        <option value="vacant">Vacant</option>
                                        <option value="occupied">Occupied</option>
                                        <option value="maintenance">Maintenance</option>
                                      </select>
                                      <button 
                                        className="btn btn-secondary" 
                                        style={{ fontSize: '12px', padding: '4px 8px' }}
                                        onClick={() => {
                                          setEditingUnit(unit);
                                          setNewUnit({
                                            unit_number: unit.unit_number,
                                            bedrooms: unit.bedrooms,
                                            bathrooms: unit.bathrooms,
                                            square_feet: unit.square_feet,
                                            rent_amount: unit.rent_amount,
                                            deposit_amount: unit.deposit_amount,
                                            building_id: unit.building_id,
                                            floor_number: unit.floor_number,
                                            unit_type: unit.unit_type
                                          });
                                          setShowEditUnitModal(true);
                                        }}
                                      >
                                        Edit
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gap: '10px' }}>
                        {units.map(unit => (
                          <div key={unit.id} style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '15px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <strong>Unit {unit.unit_number}</strong>
                                <p style={{ margin: '5px 0', color: '#666' }}>
                                  {unit.unit_type || `${unit.bedrooms}BR/${unit.bathrooms}BA`} - {unit.square_feet} sq ft
                                  {unit.floor_number && ` - Floor ${unit.floor_number}`}
                                </p>
                                <p style={{ margin: '5px 0', fontWeight: 'bold' }}>
                                  ${unit.rent_amount}/month
                                </p>
                                {unit.leases && unit.leases[0]?.residents && (
                                  <p style={{ margin: '5px 0', color: '#10b981', fontSize: '12px', fontWeight: '500' }}>
                                    👤 {unit.leases[0].residents.first_name} {unit.leases[0].residents.last_name}
                                  </p>
                                )}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <select
                                  value={unit.status}
                                  onChange={(e) => updateUnitStatus(unit.id, e.target.value)}
                                  style={{
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    border: '1px solid #ddd'
                                  }}
                                >
                                  <option value="vacant">Vacant</option>
                                  <option value="occupied">Occupied</option>
                                  <option value="maintenance">Maintenance</option>
                                </select>
                                <button 
                                  className="btn btn-secondary" 
                                  style={{ fontSize: '12px', padding: '4px 8px' }}
                                  onClick={() => {
                                    setEditingUnit(unit);
                                    setNewUnit({
                                      unit_number: unit.unit_number,
                                      bedrooms: unit.bedrooms,
                                      bathrooms: unit.bathrooms,
                                      square_feet: unit.square_feet,
                                      rent_amount: unit.rent_amount,
                                      deposit_amount: unit.deposit_amount,
                                      building_id: unit.building_id,
                                      floor_number: unit.floor_number,
                                      unit_type: unit.unit_type
                                    });
                                    setShowEditUnitModal(true);
                                  }}
                                >
                                  Edit
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'financials' && (
                  <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px' }}>
                    <h4>Financial Performance</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px', marginTop: '15px' }}>
                      <div style={{ textAlign: 'center' }}>
                        <p style={{ fontSize: '18px', fontWeight: 'bold', color: '#28a745' }}>
                          ${financialData.monthlyIncome?.toLocaleString() || '0'}
                        </p>
                        <p style={{ fontSize: '12px', color: '#666' }}>Monthly Income</p>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <p style={{ fontSize: '18px', fontWeight: 'bold', color: '#dc3545' }}>
                          ${financialData.monthlyExpenses?.toLocaleString() || '0'}
                        </p>
                        <p style={{ fontSize: '12px', color: '#666' }}>Monthly Expenses</p>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <p style={{ fontSize: '18px', fontWeight: 'bold', color: '#007bff' }}>
                          ${financialData.netIncome?.toLocaleString() || '0'}
                        </p>
                        <p style={{ fontSize: '12px', color: '#666' }}>Net Income</p>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <p style={{ fontSize: '18px', fontWeight: 'bold', color: '#17a2b8' }}>
                          {financialData.capRate || '0'}%
                        </p>
                        <p style={{ fontSize: '12px', color: '#666' }}>Cap Rate</p>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'maintenance' && (
                  <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                      <h4>Recent Maintenance</h4>
                      <button 
                        className="btn btn-primary"
                        onClick={() => window.location.href = '/maintenance'}
                      >
                        View All Maintenance
                      </button>
                    </div>
                    <div style={{ display: 'grid', gap: '10px' }}>
                      {maintenanceData.length > 0 ? maintenanceData.map(request => (
                        <div key={request.id} style={{ padding: '10px', background: '#f8f9fa', borderRadius: '4px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <strong>{request.title}</strong>
                            <span style={{
                              padding: '2px 6px',
                              borderRadius: '3px',
                              fontSize: '11px',
                              background: request.status === 'completed' ? '#d4edda' : '#fff3cd',
                              color: request.status === 'completed' ? '#155724' : '#856404'
                            }}>
                              {request.status}
                            </span>
                          </div>
                          <p style={{ margin: '5px 0', fontSize: '12px', color: '#666' }}>
                            {request.category} - {new Date(request.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      )) : (
                        <p style={{ color: '#666', textAlign: 'center' }}>No recent maintenance requests</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          </>
        )}

        {/* Empty State */}
        {properties.length === 0 && (
          <div style={{ 
            textAlign: 'center', 
            padding: '80px 40px',
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '24px' }}>🏢</div>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '24px', fontWeight: '700', color: '#1e293b' }}>
              No Properties Yet
            </h3>
            <p style={{ margin: '0 0 32px 0', color: '#64748b', fontSize: '16px', maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>
              Start building your real estate portfolio by adding your first property. Track units, manage tenants, and monitor performance all in one place.
            </p>
            <button 
              onClick={() => setShowPropertyModal(true)}
              style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                color: 'white',
                border: 'none',
                padding: '16px 32px',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
              }}
            >
              🏠 Add Your First Property
            </button>
          </div>
        )}

        {/* Add Property Modal */}
        {showPropertyModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'white', padding: '30px', borderRadius: '12px', width: '600px', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', borderBottom: '2px solid #f0f0f0', paddingBottom: '15px' }}>
                <h3 style={{ margin: 0, color: '#2c3e50', fontSize: '24px' }}>Add New Property</h3>
                <button 
                  type="button" 
                  onClick={() => setShowPropertyModal(false)}
                  style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#999' }}
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleAddProperty}>
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 15px 0', color: '#34495e', fontSize: '16px' }}>📋 Basic Information</h4>
                  <div style={{ display: 'grid', gap: '15px' }}>
                    <div className="form-group">
                      <label style={{ fontWeight: '600', color: '#2c3e50' }}>Property Name *</label>
                      <input
                        type="text"
                        value={newProperty.name}
                        onChange={(e) => setNewProperty({...newProperty, name: e.target.value})}
                        placeholder="e.g., Sunset Gardens, Oak Manor Apartments"
                        style={{ padding: '12px', border: '2px solid #e0e0e0', borderRadius: '6px', fontSize: '14px' }}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label style={{ fontWeight: '600', color: '#2c3e50' }}>Primary Address *</label>
                      <input
                        type="text"
                        value={newProperty.address}
                        onChange={(e) => setNewProperty({...newProperty, address: e.target.value})}
                        placeholder="123 Main Street, City, State 12345"
                        style={{ padding: '12px', border: '2px solid #e0e0e0', borderRadius: '6px', fontSize: '14px' }}
                        required
                      />
                      <small style={{ color: '#666', fontSize: '12px' }}>Main address or complex entrance</small>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      <div className="form-group">
                        <label style={{ fontWeight: '600', color: '#2c3e50' }}>Property Type *</label>
                        <select
                          value={newProperty.property_type}
                          onChange={(e) => setNewProperty({...newProperty, property_type: e.target.value})}
                          style={{ padding: '12px', border: '2px solid #e0e0e0', borderRadius: '6px', fontSize: '14px' }}
                        >
                          <option value="single_family">🏠 Single Family Home</option>
                          <option value="multifamily">🏘️ Multifamily (2-4 units)</option>
                          <option value="apartment_complex">🏢 Apartment Complex</option>
                          <option value="commercial">🏪 Commercial Property</option>
                          <option value="student">🎓 Student Housing</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label style={{ fontWeight: '600', color: '#2c3e50' }}>Total Units</label>
                        <input
                          type="number"
                          value={newProperty.units_count}
                          onChange={(e) => setNewProperty({...newProperty, units_count: parseInt(e.target.value) || 1})}
                          min="1"
                          style={{ padding: '12px', border: '2px solid #e0e0e0', borderRadius: '6px', fontSize: '14px' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {newProperty.property_type === 'apartment_complex' && (
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                      <h4 style={{ margin: 0, color: '#34495e', fontSize: '16px' }}>🏢 Buildings & Addresses</h4>
                      <button 
                        type="button" 
                        onClick={addBuildingToProperty}
                        style={{
                          background: '#3498db',
                          color: 'white',
                          border: 'none',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        + Add Building
                      </button>
                    </div>
                    <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                      {newProperty.buildings.map((building, index) => (
                        <div key={index} style={{ 
                          border: '2px solid #ecf0f1', 
                          borderRadius: '8px', 
                          padding: '15px', 
                          marginBottom: '15px',
                          background: '#fafafa'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <strong style={{ color: '#2c3e50', fontSize: '14px' }}>🏗️ Building {index + 1}</strong>
                            {newProperty.buildings.length > 1 && (
                              <button 
                                type="button" 
                                onClick={() => removeBuildingFromProperty(index)}
                                style={{ 
                                  color: '#e74c3c', 
                                  background: 'none', 
                                  border: 'none', 
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  fontWeight: 'bold'
                                }}
                              >
                                ✕ Remove
                              </button>
                            )}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginBottom: '12px' }}>
                            <div>
                              <label style={{ fontSize: '12px', fontWeight: '600', color: '#7f8c8d', display: 'block', marginBottom: '4px' }}>Building Name</label>
                              <input
                                type="text"
                                placeholder="e.g., Building A, North Tower"
                                value={building.building_name || ''}
                                onChange={(e) => updateBuildingInProperty(index, 'building_name', e.target.value)}
                                style={{ 
                                  width: '100%',
                                  padding: '8px', 
                                  border: '1px solid #bdc3c7', 
                                  borderRadius: '4px',
                                  fontSize: '13px'
                                }}
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '12px', fontWeight: '600', color: '#7f8c8d', display: 'block', marginBottom: '4px' }}>Street Address</label>
                              <input
                                type="text"
                                placeholder="123 Main Street, City, State 12345"
                                value={building.address || ''}
                                onChange={(e) => updateBuildingInProperty(index, 'address', e.target.value)}
                                style={{ 
                                  width: '100%',
                                  padding: '8px', 
                                  border: '1px solid #bdc3c7', 
                                  borderRadius: '4px',
                                  fontSize: '13px'
                                }}
                              />
                            </div>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <div>
                              <label style={{ fontSize: '12px', fontWeight: '600', color: '#7f8c8d', display: 'block', marginBottom: '4px' }}>Number of Floors</label>
                              <input
                                type="number"
                                placeholder="5"
                                value={building.floors || 1}
                                onChange={(e) => updateBuildingInProperty(index, 'floors', parseInt(e.target.value) || 1)}
                                min="1"
                                style={{ 
                                  width: '100%',
                                  padding: '8px', 
                                  border: '1px solid #bdc3c7', 
                                  borderRadius: '4px',
                                  fontSize: '13px'
                                }}
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '12px', fontWeight: '600', color: '#7f8c8d', display: 'block', marginBottom: '4px' }}>Units per Floor</label>
                              <input
                                type="number"
                                placeholder="6"
                                value={building.units_per_floor || 1}
                                onChange={(e) => updateBuildingInProperty(index, 'units_per_floor', parseInt(e.target.value) || 1)}
                                min="1"
                                style={{ 
                                  width: '100%',
                                  padding: '8px', 
                                  border: '1px solid #bdc3c7', 
                                  borderRadius: '4px',
                                  fontSize: '13px'
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div style={{ 
                  display: 'flex', 
                  gap: '12px', 
                  marginTop: '25px', 
                  paddingTop: '20px', 
                  borderTop: '2px solid #f0f0f0',
                  justifyContent: 'flex-end'
                }}>
                  <button 
                    type="button" 
                    onClick={() => setShowPropertyModal(false)}
                    style={{
                      padding: '12px 24px',
                      border: '2px solid #bdc3c7',
                      borderRadius: '6px',
                      background: 'white',
                      color: '#7f8c8d',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    style={{
                      padding: '12px 24px',
                      border: 'none',
                      borderRadius: '6px',
                      background: '#27ae60',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    🏠 Create Property
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add Unit Modal */}
        {showUnitModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'white', padding: '30px', borderRadius: '8px', width: '500px' }}>
              <h3>Add New Unit</h3>
              <form onSubmit={handleAddUnit}>
                <div className="form-group">
                  <label>Unit Number *</label>
                  <input
                    type="text"
                    value={newUnit.unit_number}
                    onChange={(e) => setNewUnit({...newUnit, unit_number: e.target.value})}
                    required
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label>Bedrooms</label>
                    <input
                      type="number"
                      value={newUnit.bedrooms}
                      onChange={(e) => setNewUnit({...newUnit, bedrooms: parseInt(e.target.value)})}
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label>Bathrooms</label>
                    <input
                      type="number"
                      step="0.5"
                      value={newUnit.bathrooms}
                      onChange={(e) => setNewUnit({...newUnit, bathrooms: parseFloat(e.target.value)})}
                      min="0"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Square Feet</label>
                  <input
                    type="number"
                    value={newUnit.square_feet}
                    onChange={(e) => setNewUnit({...newUnit, square_feet: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Monthly Rent *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newUnit.rent_amount}
                    onChange={(e) => setNewUnit({...newUnit, rent_amount: e.target.value})}
                    required
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label>Floor Number</label>
                    <input
                      type="number"
                      value={newUnit.floor_number}
                      onChange={(e) => setNewUnit({...newUnit, floor_number: e.target.value})}
                      min="1"
                      placeholder="e.g., 3"
                    />
                  </div>
                  <div className="form-group">
                    <label>Unit Type</label>
                    <select
                      value={newUnit.unit_type}
                      onChange={(e) => setNewUnit({...newUnit, unit_type: e.target.value})}
                    >
                      <option value="studio">Studio</option>
                      <option value="1br">1 Bedroom</option>
                      <option value="2br">2 Bedroom</option>
                      <option value="3br">3 Bedroom</option>
                      <option value="4br">4+ Bedroom</option>
                      <option value="penthouse">Penthouse</option>
                    </select>
                  </div>
                </div>
                {selectedProperty?.property_type === 'apartment_complex' && buildings.length > 0 && (
                  <div className="form-group">
                    <label>Building</label>
                    <select
                      value={newUnit.building_id || ''}
                      onChange={(e) => setNewUnit({...newUnit, building_id: e.target.value})}
                    >
                      <option value="">Select Building</option>
                      {buildings.map(building => (
                        <option key={building.id} value={building.id}>
                          {building.building_name} - {building.address}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button type="submit" className="btn btn-primary">Add Unit</button>
                  <button type="button" onClick={() => setShowUnitModal(false)} className="btn btn-secondary">Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Property Modal */}
        {showEditPropertyModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'white', padding: '30px', borderRadius: '8px', width: '500px' }}>
              <h3>Edit Property</h3>
              <form onSubmit={handleEditProperty}>
                <div className="form-group">
                  <label>Property Name *</label>
                  <input
                    type="text"
                    value={editProperty.name || ''}
                    onChange={(e) => setEditProperty({...editProperty, name: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Address *</label>
                  <input
                    type="text"
                    value={editProperty.address || ''}
                    onChange={(e) => setEditProperty({...editProperty, address: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Property Type</label>
                  <select
                    value={editProperty.property_type || 'multifamily'}
                    onChange={(e) => setEditProperty({...editProperty, property_type: e.target.value})}
                  >
                    <option value="single_family">Single Family</option>
                    <option value="multifamily">Multifamily</option>
                    <option value="apartment_complex">Apartment Complex</option>
                    <option value="commercial">Commercial</option>
                    <option value="student">Student Housing</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Year Built</label>
                  <input
                    type="number"
                    value={editProperty.year_built || ''}
                    onChange={(e) => setEditProperty({...editProperty, year_built: e.target.value ? parseInt(e.target.value) : ''})}
                  />
                </div>
                
                {editProperty.property_type === 'apartment_complex' && (
                  <div className="form-group">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <label>Buildings</label>
                      <button 
                        type="button" 
                        onClick={addBuildingToEdit}
                        style={{
                          background: '#3498db',
                          color: 'white',
                          border: 'none',
                          padding: '6px 10px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        + Add Building
                      </button>
                    </div>
                    <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                      {editBuildings.map((building, index) => (
                        <div key={index} style={{ 
                          border: '1px solid #ddd', 
                          borderRadius: '4px', 
                          padding: '10px', 
                          marginBottom: '10px',
                          background: '#f9f9f9'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <strong style={{ fontSize: '13px' }}>Building {index + 1}</strong>
                            <button 
                              type="button" 
                              onClick={() => removeBuildingFromEdit(index)}
                              style={{ 
                                color: '#e74c3c', 
                                background: 'none', 
                                border: 'none', 
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              Remove
                            </button>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '8px', marginBottom: '8px' }}>
                            <input
                              type="text"
                              placeholder="Building Name"
                              value={building.building_name || ''}
                              onChange={(e) => updateBuildingInEdit(index, 'building_name', e.target.value)}
                              style={{ padding: '6px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '12px' }}
                            />
                            <input
                              type="text"
                              placeholder="Address"
                              value={building.address || ''}
                              onChange={(e) => updateBuildingInEdit(index, 'address', e.target.value)}
                              style={{ padding: '6px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '12px' }}
                            />
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            <input
                              type="number"
                              placeholder="Floors"
                              value={building.floors || 1}
                              onChange={(e) => updateBuildingInEdit(index, 'floors', parseInt(e.target.value) || 1)}
                              min="1"
                              style={{ padding: '6px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '12px' }}
                            />
                            <input
                              type="number"
                              placeholder="Units per Floor"
                              value={building.units_per_floor || 1}
                              onChange={(e) => updateBuildingInEdit(index, 'units_per_floor', parseInt(e.target.value) || 1)}
                              min="1"
                              style={{ padding: '6px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '12px' }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button type="submit" className="btn btn-primary">Update Property</button>
                  <button type="button" onClick={() => setShowEditPropertyModal(false)} className="btn btn-secondary">Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add Building Modal */}
        {showBuildingModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'white', padding: '30px', borderRadius: '8px', width: '500px' }}>
              <h3>Add New Building</h3>
              <form onSubmit={handleAddBuilding}>
                <div className="form-group">
                  <label>Building Name *</label>
                  <input
                    type="text"
                    value={newBuilding.building_name}
                    onChange={(e) => setNewBuilding({...newBuilding, building_name: e.target.value})}
                    placeholder="e.g., Building A, North Tower"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Building Number</label>
                  <input
                    type="text"
                    value={newBuilding.building_number}
                    onChange={(e) => setNewBuilding({...newBuilding, building_number: e.target.value})}
                    placeholder="e.g., 1, A, North"
                  />
                </div>
                <div className="form-group">
                  <label>Address *</label>
                  <input
                    type="text"
                    value={newBuilding.address}
                    onChange={(e) => setNewBuilding({...newBuilding, address: e.target.value})}
                    placeholder="123 Main Street, City, State 12345"
                    required
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label>Number of Floors *</label>
                    <input
                      type="number"
                      value={newBuilding.floors}
                      onChange={(e) => setNewBuilding({...newBuilding, floors: parseInt(e.target.value) || 1})}
                      min="1"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Units per Floor *</label>
                    <input
                      type="number"
                      value={newBuilding.units_per_floor}
                      onChange={(e) => setNewBuilding({...newBuilding, units_per_floor: parseInt(e.target.value) || 1})}
                      min="1"
                      required
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button type="submit" className="btn btn-primary">Add Building</button>
                  <button type="button" onClick={() => setShowBuildingModal(false)} className="btn btn-secondary">Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Unit Modal */}
        {showEditUnitModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'white', padding: '30px', borderRadius: '8px', width: '500px' }}>
              <h3>Edit Unit</h3>
              <form onSubmit={handleEditUnit}>
                <div className="form-group">
                  <label>Unit Number *</label>
                  <input
                    type="text"
                    value={newUnit.unit_number || ''}
                    onChange={(e) => setNewUnit({...newUnit, unit_number: e.target.value})}
                    required
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label>Bedrooms</label>
                    <input
                      type="number"
                      value={newUnit.bedrooms || 1}
                      onChange={(e) => setNewUnit({...newUnit, bedrooms: parseInt(e.target.value)})}
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label>Bathrooms</label>
                    <input
                      type="number"
                      step="0.5"
                      value={newUnit.bathrooms || 1}
                      onChange={(e) => setNewUnit({...newUnit, bathrooms: parseFloat(e.target.value)})}
                      min="0"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Square Feet</label>
                  <input
                    type="number"
                    value={newUnit.square_feet || ''}
                    onChange={(e) => setNewUnit({...newUnit, square_feet: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Monthly Rent *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newUnit.rent_amount || ''}
                    onChange={(e) => setNewUnit({...newUnit, rent_amount: e.target.value})}
                    required
                  />
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button type="submit" className="btn btn-primary">Update Unit</button>
                  <button type="button" onClick={() => setShowEditUnitModal(false)} className="btn btn-secondary">Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Properties;